import { forwardRef, type SelectHTMLAttributes } from 'react';
import { useTranslation } from 'react-i18next';
import { useEquipment } from '@/features/equipment/queries';
import { cn } from '@/lib/utils';

interface ParentEquipmentPickerProps
  extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'onChange' | 'value'> {
  /** Vessel scope. The picker fetches /equipment?vesselId=… and is
   *  disabled when no vessel is selected (the picker can't choose a
   *  parent without knowing the vessel first). */
  vesselId: string | null;
  value: string | null;
  onChange: (parentId: string | null) => void;
  /** When editing, pass the current equipment id so it gets excluded
   *  from the option list — an equipment can't be its own parent. */
  excludeId?: string;
}

/**
 * ParentEquipmentPicker — native <select> showing every Equipment
 * from the same vessel, suitable as a parent for a new (or edited)
 * sub-component. Shows "— None (top-level) —" as the default option.
 *
 * NOTE: this picker is intentionally flat for F4.2; it does not yet
 * detect cycles when a parent is reparented to one of its descendants.
 * The backend rejects such mutations (Phase 3a cycle detection); the
 * UI currently surfaces that as a regular ApiError. We can add a
 * client-side pre-check in F4.3 when the tree data is already loaded.
 */
export const ParentEquipmentPicker = forwardRef<
  HTMLSelectElement,
  ParentEquipmentPickerProps
>(({ vesselId, value, onChange, excludeId, className, ...rest }, ref) => {
  const { t } = useTranslation();
  const { data, isLoading } = useEquipment({
    vesselId: vesselId ?? undefined,
  });

  const items = (data ?? []).filter((e) => e.id !== excludeId);

  return (
    <select
      ref={ref}
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value === '' ? null : e.target.value)}
      disabled={!vesselId || isLoading || rest.disabled}
      className={cn(
        'flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
        className,
      )}
      {...rest}
    >
      <option value="">{t('equipment.fields.parentEquipmentHint')}</option>
      {items.map((e) => (
        <option key={e.id} value={e.id}>
          {e.tag} — {e.name}
        </option>
      ))}
    </select>
  );
});
ParentEquipmentPicker.displayName = 'ParentEquipmentPicker';
